import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Youtube, Music2, ExternalLink, Instagram } from 'lucide-react';
import { motion } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a027839a-8849-4776-a8ba-c961dd10b69f/artifacts/co2pjy4b_IMG_20260323_062123.jpg";

const TikTokMini = () => (
  <svg width={10} height={10} viewBox="0 0 24 24" fill="currentColor" className="text-[#00f2ea]">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z"/>
  </svg>
);

export default function EpisodeCard({ episode, index = 0 }) {
  const extractYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/]+)/);
    return match ? match[1] : null;
  };

  const ytId = extractYoutubeId(episode.youtube_url);
  const thumbnail = episode.thumbnail_url || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : LOGO_URL);
  const reels = episode.reels || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -6 }}
      whileTap={{ y: -6 }}
      viewport={{ once: true }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      data-testid={`episode-card-${episode.id}`}
      className="group bg-[#141414] rounded-xl border border-white/5 hover:border-[#FF2A2A]/30 overflow-hidden hover:shadow-[0_16px_40px_rgba(0,0,0,0.35),0_0_30px_rgba(255,42,42,0.06)] cursor-pointer"
    >
      {/* Thumbnail */}
      <Link to={`/episode/${episode.id}`} className="relative aspect-video overflow-hidden bg-[#1C1C1C] block">
        <img
          src={thumbnail}
          alt={episode.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="relative w-14 h-14 rounded-full bg-[#FF2A2A] flex items-center justify-center shadow-lg shadow-[#FF2A2A]/30 play-pulse">
            <Play size={22} fill="white" className="text-white ml-0.5" />
          </div>
        </div>
        <div className="absolute top-3 left-3 bg-[#FF2A2A] px-3 py-1 rounded-md">
          <span className="font-body text-xs font-bold text-white uppercase tracking-wider">EP {episode.episode_number}</span>
        </div>
        <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm px-2.5 py-1 rounded-md">
          <span className="font-body text-[10px] font-semibold text-white/90 uppercase tracking-wider">
            {episode.duration || `${Math.floor(35 + (episode.episode_number * 2.3) % 30)} min`}
          </span>
        </div>
      </Link>

      <div className="p-5">
        <h3 className="font-heading text-xl uppercase mb-2 line-clamp-2 group-hover:text-[#FF2A2A] transition-colors">
          {episode.title}
        </h3>
        {episode.description && (
          <p className="font-body text-sm text-[#A1A1AA] line-clamp-2 mb-4 leading-relaxed">{episode.description}</p>
        )}
        <div className="flex items-center gap-3">
          {episode.youtube_url && (
            <a href={episode.youtube_url} target="_blank" rel="noopener noreferrer"
              data-testid={`episode-youtube-${episode.id}`}
              className="flex items-center gap-1.5 text-[#A1A1AA] hover:text-[#FF2A2A] transition-colors text-xs font-body font-semibold">
              <Youtube size={14} /> YouTube
            </a>
          )}
          {episode.spotify_url && (
            <a href={episode.spotify_url} target="_blank" rel="noopener noreferrer"
              data-testid={`episode-spotify-${episode.id}`}
              className="flex items-center gap-1.5 text-[#A1A1AA] hover:text-[#1DB954] transition-colors text-xs font-body font-semibold">
              <Music2 size={14} /> Spotify
            </a>
          )}
          <Link to={`/episode/${episode.id}`} data-testid={`episode-view-${episode.id}`}
            className="ml-auto text-xs font-body font-semibold text-[#FF2A2A] hover:underline">
            Listen
          </Link>
        </div>
      </div>

      {/* Reels Row */}
      {reels.length > 0 && (
        <div className="px-5 pb-5 pt-0" data-testid={`episode-reels-${episode.id}`}>
          <div className="border-t border-white/5 pt-4">
            <p className="font-body text-[10px] font-bold text-[#FF2A2A] uppercase tracking-[0.15em] mb-3">
              REELS FROM THIS EPISODE
            </p>
            <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide snap-x snap-mandatory">
              {reels.map((reel, ri) => (
                <motion.a
                  key={ri}
                  href={reel.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -6 }}
                  whileTap={{ y: -6 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  data-testid={`reel-tile-${episode.id}-${ri}`}
                  className="relative shrink-0 w-[90px] aspect-[9/16] rounded-lg overflow-hidden bg-[#1C1C1C] border border-white/5 hover:border-[#FF2A2A]/40 hover:shadow-[0_12px_24px_rgba(0,0,0,0.3),0_0_20px_rgba(255,42,42,0.06)] snap-start cursor-pointer"
                >
                  {(reel.thumbnail || thumbnail) ? (
                    <img src={reel.thumbnail || thumbnail} alt={reel.label || 'Reel'} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#FF2A2A]/10 to-transparent" />
                  )}
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center">
                    <Play size={10} fill="white" className="text-white ml-px" />
                  </div>
                  {/* Platform badge */}
                  <div className="absolute top-2 left-2 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center">
                    {reel.platform === 'tiktok' ? <TikTokMini /> : <Instagram size={10} className="text-[#E1306C]" />}
                  </div>
                  {reel.label && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <p className="font-body text-[9px] text-white/90 font-semibold leading-tight line-clamp-2">{reel.label}</p>
                    </div>
                  )}
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
